/**
 * PreloadScene — the calm loading screen. Fetches the local GameDataset via the
 * data loader (NEVER the live API; fixtures fallback baked into the loader), shows
 * a soft progress bar, then routes to the title flow: Continue (a save exists) or
 * New Game (fresh). The dataset + a shared SpriteResolver are stashed on the game
 * registry for feature scenes to read.
 *
 * Loading the dataset is a single fetch (not many Phaser file loads), so the
 * progress UI animates gently to "ready" rather than jittering per-file.
 */
import Phaser from 'phaser';
import { loadDataset, loadDataIndex, loadRenderIndex, type DataIndex } from '@/data';
import { loadGame, setMigrationDataLookup } from '@/store';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './ui/theme.js';
import { Layout } from './layout/index.js';
import { SpriteResolver } from './sprites/index.js';
import { fadeIn, goTo, setReducedMotion } from './flow.js';
import { FX_URL_BASE, setFxCatalog, type FxCatalog } from './effects/showdownFx.js';

/** Registry keys other scenes read shared infrastructure from. */
export const REGISTRY_DATASET = 'pr:dataset';
export const REGISTRY_DATA_INDEX = 'pr:dataIndex';
export const REGISTRY_SPRITES = 'pr:sprites';
export const REGISTRY_HAS_SAVE = 'pr:hasSave';
/**
 * `true` once every Showdown fx PNG (client/data/generated/fx/catalog.json) has
 * been loaded into Phaser's texture cache, so {@link import('./BattleScene').BattleScene}
 * can safely play the ported Showdown move animations. When `false` (the catalog
 * is missing, or a fetch/load failed), BattleScene quietly uses its procedural
 * fallback instead — a missing art pack never blocks a battle.
 */
export const REGISTRY_FX_READY = 'pr:fxReady';
/**
 * The live {@link import('@/session').GameSession} once the entry flow (Title/NewGame)
 * has created or resumed it. Every feature scene reads the session from here:
 *   const session = this.registry.get(REGISTRY_SESSION) as GameSession;
 * It is `undefined` until the player passes through Title -> (Continue | NewGame),
 * which is why feature scenes are only ever reached *after* the session is set.
 */
export const REGISTRY_SESSION = 'pr:session';

export class PreloadScene extends Phaser.Scene {
  private layout!: Layout;
  private barBg?: Phaser.GameObjects.Graphics;
  private barFill?: Phaser.GameObjects.Graphics;
  private titleText?: Phaser.GameObjects.Text;
  private statusText?: Phaser.GameObjects.Text;
  private progress = 0;

  /**
   * Whether the Showdown fx pack finished loading into Phaser's texture cache. Set in
   * {@link preload} (the only phase Phaser runs the loader) from the loader's COMPLETE
   * event, then read in {@link boot} to publish {@link REGISTRY_FX_READY}. Stays `false`
   * if the catalog is missing or every PNG failed — BattleScene falls back procedurally.
   */
  private fxReady = false;

  constructor() {
    super('Preload');
  }

  /**
   * PRELOAD phase — this is the only place Phaser actually runs the loader, so the
   * Showdown battle-fx PNGs MUST be queued here (queuing them later, during create(),
   * leaves them stranded in the queue and the textures never load).
   *
   * Sequencing: the fx catalog is async (a JSON fetch), so we let Phaser's own loader
   * fetch it (it is auto-started + auto-pumped during preload). The catalog's
   * file-complete callback fires WHILE the loader is still running; from there we
   * register the catalog and queue every PNG via this.load.image(...). Files added to
   * an in-flight loader are picked up before it emits COMPLETE, so all the textures
   * finish loading within this same preload pass — no manual pump, no create()-phase
   * queueing. If the catalog 404s / fails to parse, we simply queue nothing and the fx
   * stay unavailable (fxReady = false → BattleScene uses its procedural fallback).
   */
  preload(): void {
    const FX_CATALOG_KEY = 'pr:fxCatalog';
    this.load.json(FX_CATALOG_KEY, `${FX_URL_BASE}/catalog.json`);

    // Catalog arrived: register it + queue each PNG into the still-running loader.
    this.load.once(
      `${Phaser.Loader.Events.FILE_KEY_COMPLETE}json-${FX_CATALOG_KEY}`,
      (_key: string, _type: string, catalog: FxCatalog) => {
        if (!catalog || typeof catalog !== 'object') return;
        setFxCatalog(catalog);
        for (const entry of Object.values(catalog)) {
          if (entry?.key && entry?.file && !this.textures.exists(entry.key)) {
            this.load.image(entry.key, `${FX_URL_BASE}/${entry.file}`);
          }
        }
      },
    );

    // Loader finished (catalog + every queued PNG): fx are ready iff the catalog
    // registered and at least one of its textures is now in the cache. A per-file
    // error never aborts the batch, so a partial pack still reports ready.
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      const catalog = (this.cache.json.get(FX_CATALOG_KEY) as FxCatalog | undefined) ?? null;
      const keys = catalog ? Object.keys(catalog) : [];
      this.fxReady = keys.length > 0 && keys.some((k) => this.textures.exists(k));
    });
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);

    this.titleText = this.add
      .text(0, 0, 'Parallel Routes', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.title}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.statusText = this.add
      .text(0, 0, 'waking the town…', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    this.barBg = this.add.graphics();
    this.barFill = this.add.graphics();

    this.relayout();
    this.layout.on('change', () => this.relayout());
    fadeIn(this);

    void this.boot();
  }

  /** Fetch the world data, build the shared infra, then route to the title flow. */
  private async boot(): Promise<void> {
    this.setProgress(0.1, 'reading the dex…');
    let dataIndex: DataIndex;
    try {
      const dataset = await loadDataset();
      dataIndex = await loadDataIndex();
      this.registry.set(REGISTRY_DATASET, dataset);
      this.registry.set(REGISTRY_DATA_INDEX, dataIndex);
      // Wire the dataset into the unlimited-moveset save migration BEFORE loadGame() (which
      // migrates the save below): the v4→v5 fixup fills each mon's full natural moveset.
      setMigrationDataLookup(dataIndex);
      this.setProgress(
        0.6,
        dataset.fromGenerated ? 'sprites & species ready…' : 'starter set ready…',
      );
    } catch {
      // The loader never throws, but stay defensive: a tired player never sees a crash.
      this.setProgress(0.6, 'starter set ready…');
    }

    // A shared, scene-agnostic resolver instance lives on this (persistent) scene's
    // texture manager so pooled textures survive scene swaps. Feature scenes read it.
    // Install the 3D-render manifest so battles + the dex PREFER the animated renders
    // (per-side fallback to the Gen5 chain when a render is missing; never throws).
    const resolver = new SpriteResolver(this);
    try {
      resolver.setRenderIndex(await loadRenderIndex());
    } catch {
      // loadRenderIndex never throws, but stay defensive — a missing manifest just
      // means battles/dex use the Gen5 sprite chain. No crash, ever.
    }
    this.registry.set(REGISTRY_SPRITES, resolver);

    // Publish whether the Showdown battle-fx pack loaded (resolved during preload(),
    // where Phaser actually runs the loader). When false — the catalog/art was missing
    // or a load failed — BattleScene quietly uses its procedural fallback, so a missing
    // art pack never blocks the game (CLAUDE.md fallback discipline).
    this.setProgress(0.72, 'gathering battle sparks…');
    this.registry.set(REGISTRY_FX_READY, this.fxReady);

    this.setProgress(0.8, 'checking your save…');
    let hasSave: boolean;
    try {
      const save = await loadGame();
      hasSave = save != null;
      if (save) setReducedMotion(save.settings.reducedMotion);
    } catch {
      hasSave = false;
    }
    this.registry.set(REGISTRY_HAS_SAVE, hasSave);

    this.setProgress(1, hasSave ? 'welcome back…' : 'a new adventure…');
    this.time.delayedCall(450, () => {
      // The proxy PASSWORD GATE runs next: it probes /api/health and, when the proxy
      // has a password configured and we hold no token yet, prompts once and exchanges
      // it for the bearer token saves + chat need. It AUTO-SKIPS straight to Title when
      // the gate isn't needed (token present, proxy unreachable, or no password set), so
      // the flow stays calm: Boot -> Preload -> AuthGate -> Title -> (Town | NewGame).
      goTo(this, 'AuthGate', { data: { fromPreload: true } });
    });
  }

  private setProgress(value: number, status: string): void {
    this.progress = Phaser.Math.Clamp(value, 0, 1);
    this.statusText?.setText(status);
    this.drawBar();
  }

  private relayout(): void {
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;
    this.titleText?.setPosition(cx, cy - SPACING.xxl);
    this.statusText?.setPosition(cx, cy + SPACING.lg);
    this.drawBar();
  }

  private drawBar(): void {
    if (!this.barBg || !this.barFill) return;
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;
    const w = Math.min(360, this.layout.safe.width - SPACING.xxl);
    const h = 8;
    const x = cx - w / 2;
    const y = cy;

    this.barBg.clear();
    this.barBg.fillStyle(COLORS.surfaceAlt, 1);
    this.barBg.fillRoundedRect(x, y, w, h, RADIUS.sm);

    this.barFill.clear();
    this.barFill.fillStyle(COLORS.accent, 1);
    const fillW = Math.max(h, w * this.progress);
    this.barFill.fillRoundedRect(x, y, fillW, h, RADIUS.sm);
  }
}
